import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import './FigureTable.css';

// A figure's table drawn as a real HTML table (2026-11-02). Reading and Writing
// data questions (Command of Evidence: Quantitative, 213 in the curriculum
// bank) store their table twice: a baked SVG and `figure.table`
// ({ caption, headers, rows }). The SVG is drawn up to 1,531px wide with 14px
// text, and figures always fit their slot (SvgFigure), so in the question
// column it shrank to unreadable 5px text (Sandy 2026-11-02, a 40-question
// assessment). From the data, the text keeps a reading size; in a narrow box
// (the teacher's ready card, a phone) it tightens to 11-13px (FigureTable.css).
//
// A table wider than its box tries, in order, until one fits: upright; upright
// and tight; on its side (when that adds no columns: the long headers become
// row labels with room to wrap); on its side and tight; and last, one block
// per row ("Header: value" lines), which always fits. Nothing in a cell breaks
// inside a word or splits what belongs together (glue below): the review of all
// 213 found "teenager|s" chopped in the cell a key turns on, "34 (bandleader)"
// wrapped into the distractor's "(bandleader), 19", and "TL-/406" beside
// TL-460. Cells are plain text, never LaTeX: 36 cells hold money like "$4.50",
// which a math renderer would read as the start of a formula.

// figure -> { caption, headers, rows, notes } (every row padded to the widest), or null. A one-cell row in a
// wider table is a footnote ("Note: Percentages in a row may not total 100 because of rounding.", 3 tables):
// it is shown under the table, not as a row of blanks.
export function figureTableOf(figure) {
  const t = figure && figure.table;
  if (!t || !Array.isArray(t.rows)) return null;
  const all = t.rows.filter(Array.isArray);
  const headers = Array.isArray(t.headers) ? t.headers : [];
  const cols = Math.max(headers.length, ...all.map((r) => r.length));
  const isNote = (r) => cols > 1 && r.length === 1;
  const rows = all.filter((r) => !isNote(r));
  if (!rows.length || !cols) return null;
  const pad = (r) => Array.from({ length: cols }, (_, i) => (r[i] == null ? '' : String(r[i])));
  return {
    caption: t.caption ? String(t.caption) : '', headers: headers.length ? pad(headers) : null, rows: rows.map(pad),
    notes: all.filter(isNote).map((r) => String(r[0])),
  };
}

// The table on its side: the first column's values (years, towns) become the top row, under the first header
// as a label; every other column becomes a row headed by its header. Worth it when that adds no columns.
export function sideways(table) {
  const head = table.headers || table.rows[0].map(() => '');
  return {
    label: head[0],
    top: table.rows.map((r) => r[0]),
    rows: head.slice(1).map((h, c) => [h, ...table.rows.map((r) => r[c + 1])]),
  };
}
const canTurn = (table) => table.rows.length + 1 <= (table.headers || table.rows[0]).length;

// A long unit the headers share ("Change in total spending (percentage points)", three columns) moves to the end
// of the caption, so the headers get short and the table can stay a table (questions 145-147 compare one column
// across five periods). Only when every header that ends in a parenthesis ends in that same one, so the caption's
// unit is true of every column that had a unit. -> the table with the unit moved, or null.
const TAIL = /\s*(\([^()]+\))\s*$/;
export function shareUnit(table) {
  if (!table.headers) return null;
  const tails = table.headers.map((h) => { const m = TAIL.exec(h); return m ? m[1] : null; });
  const unit = tails.find(Boolean);
  if (!unit || unit.length < 6 || tails.filter((x) => x === unit).length < 2 || tails.some((x) => x && x !== unit)) return null;
  return {
    ...table,
    caption: table.caption ? `${table.caption} ${unit}` : unit,
    headers: table.headers.map((h, i) => (tails[i] ? h.replace(TAIL, '') : h)),
  };
}
// Runs that never wrap inside: a number range with its unit ("0–20 m", "1928–1948", "2.1–3.0", "360 to 400"), a
// word or code with an inner hyphen ("TL-406", "E-reader"), a number with its parenthesis ("34 (bandleader)") or its
// short unit ("20 m", "45 %"). A cell with no spaces never wraps at all.
const NUM = /[−-]?\$?\d[\d.,]*/.source;
// A short unit ("m", "km", "%", "°C", "m/s"); a word unit like "years" may wrap below its number, or a long one
// ("60,000–90,000 years") keeps a whole table from fitting.
const UNIT = '(?:%|[A-Za-zµ°²³]{1,3}(?:/[A-Za-z]{1,3})?)(?![A-Za-z])';
const GLUE = new RegExp([
  `${NUM}\\s?[–—-]\\s?${NUM}(?:\\s?${UNIT})?`,
  `${NUM}\\s+(?:to|and)\\s+${NUM}(?:\\s?${UNIT})?`,
  /[A-Za-z0-9.,$]+(?:[-‐‑][A-Za-z0-9.,]+)+/.source,
  // a number with a one-word parenthesis ("34 (bandleader)"); a longer one ("2011 (millions of kilograms)") may wrap
  `${NUM}\\s\\([^)\\s]{1,20}\\)`,
  `${NUM}\\s${UNIT}`,
  // a label with its small number or letter ("Thread 1", "piece 2", "Pellish A")
  /\b[A-Za-z]+\s(?:\d{1,3}|[A-Z])(?![\w.,–—-]|\s+(?:to|and)\s)/.source,
  // a word with a short unit in parentheses ("Increase (%)")
  /[A-Za-z]+\s\([^()\s]{1,4}\)/.source,
  // a short parenthesis ("(% change)"), a genus initial with its species ("T. vellosa"), "et al."
  /\([^()\s]{1,3}\s[^()]{1,16}\)/.source,
  /\b[A-Z]\.\s[a-z]+/.source,
  /\bet\sal\./.source,
].join('|'), 'g');
export function glue(text) {
  const s = String(text == null ? '' : text);
  if (!/\s/.test(s)) return s ? <span className="bb-nb">{s}</span> : s;
  const out = [];
  let last = 0;
  GLUE.lastIndex = 0;
  for (let m = GLUE.exec(s); m; m = GLUE.exec(s)) {
    if (!m[0].length) { GLUE.lastIndex += 1; continue; }
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push(<span key={m.index} className="bb-nb">{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}

const LAYOUTS = (table) => [
  { turn: false, tight: false },
  { turn: false, tight: true },
  ...(shareUnit(table) ? [{ unit: true, tight: false }, { unit: true, tight: true }] : []),
  ...(canTurn(table) ? [{ turn: true, tight: false }, { turn: true, tight: true }] : []),
  { stack: true },
];

const Cell = ({ as: Tag = 'td', c, ...rest }) => <Tag {...rest}>{glue(c)}</Tag>;

export default function FigureTable({ table }) {
  const id = useId();
  const scroller = useRef(null);
  const layouts = table ? LAYOUTS(table) : [];
  const [at, setAt] = useState(0);
  // try each layout until one fits its box; a wider box (resize) starts again from the first
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || !table) return undefined;
    let width = el.clientWidth;
    let live = true;
    // exact: a table 1px wider than its box clips its right border, so it does not fit
    const check = () => { if (live && at < layouts.length - 1 && el.scrollWidth > el.clientWidth) setAt(at + 1); };
    check();
    // the box (a resize) and the table itself: a web font that loads after the first check widens the table
    // without resizing the box (question 121 stayed 8px too wide on a phone)
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
      if (el.clientWidth > width + 1 && at > 0) { width = el.clientWidth; setAt(0); return; }
      width = el.clientWidth;
      check();
    }) : null;
    if (ro) { ro.observe(el); if (el.firstElementChild) ro.observe(el.firstElementChild); }
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) document.fonts.ready.then(check);
    return () => { live = false; if (ro) ro.disconnect(); };
  }, [table, at, layouts.length]);
  if (!table) return null;
  const layout = layouts[Math.min(at, layouts.length - 1)];
  // the upright table as drawn: with a shared unit moved to the caption on the unit steps
  const view = (layout.unit && shareUnit(table)) || table;
  const labelled = table.caption ? `${id}-cap` : undefined;
  const heads = table.headers || table.rows[0].map(() => '');

  let body;
  if (layout.stack) {
    // one block per row: the first column's value as its heading, then "Header: value" for the rest
    body = (
      <div className="bb-fstack" role="list" aria-labelledby={labelled}>
        {table.rows.map((r, i) => (
          <div key={i} className="bb-fstack-row" role="listitem">
            <p className="bb-fstack-head">{heads[0] ? <span className="bb-fstack-key">{glue(heads[0])}{': '}</span> : null}{glue(r[0])}</p>
            {r.slice(1).map((c, j) => (
              <p key={j} className="bb-fstack-line"><span className="bb-fstack-key">{glue(heads[j + 1])}{': '}</span><span className="bb-fstack-val">{glue(c)}</span></p>
            ))}
          </div>
        ))}
      </div>
    );
  } else if (layout.turn) {
    const s = sideways(table);
    body = (
      <table className={`bb-ftable is-turned${layout.tight ? ' is-tight' : ''}`} aria-labelledby={labelled}>
        <thead>
          {s.label && <tr><td className="bb-ftable-corner" /><th scope="colgroup" colSpan={s.top.length}>{glue(s.label)}</th></tr>}
          <tr><td className="bb-ftable-corner" />{s.top.map((c, i) => <Cell key={i} as="th" scope="col" c={c} />)}</tr>
        </thead>
        <tbody>
          {s.rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => (j === 0 ? <Cell key={j} as="th" scope="row" c={c} /> : <Cell key={j} c={c} />))}</tr>
          ))}
        </tbody>
      </table>
    );
  } else {
    body = (
      <table className={`bb-ftable${layout.tight ? ' is-tight' : ''}`} aria-labelledby={labelled}>
        {view.headers && (
          <thead>
            <tr>{view.headers.map((c, i) => <Cell key={i} as="th" scope="col" c={c} />)}</tr>
          </thead>
        )}
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i}>{r.map((c, j) => <Cell key={j} c={c} />)}</tr>
          ))}
        </tbody>
      </table>
    );
  }
  return (
    <div className="bb-ftable-box" lang="en">
      {view.caption && <p className="bb-ftable-cap" id={`${id}-cap`}>{glue(view.caption)}</p>}
      <div className="bb-ftable-scroll" ref={scroller}>{body}</div>
      {(table.notes || []).map((n, i) => <p key={i} className="bb-ftable-note">{glue(n)}</p>)}
    </div>
  );
}
